import { SearchRounded } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Grid,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useColors from "../../../../../api/hooks/useColors";
import TextButton from "../../../../../components/buttons/TextButton";
import iconMapping from "../../../../../icons/IconMapping";
import SvgToIcon from "../../../../../icons/SvgToIcon";
import { AssetClass } from "../../../../../utils/constants";
import AssetDetailsBox from "./AssetDetailsBox";
import EntitiesAssetWizardTable from "./EntitiesAssetWizardTable";

const ManualAddStep = ({
  setFormData,
  formData,
  allEntities,
  fetchedEntitiesAreLoading,
  assetToEdit,
}) => {
  const colors = useColors();
  const [selectedItem, setSelectedItem] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [entityExists, setEntityExists] = useState(false);
  const autocompleteRef = useRef(null);
  const [resetKey, setResetKey] = useState(0); // Add a key to force re-render

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    setEntityExists(false); // Reset validation state when input changes
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && selectedItem != null) {
      handleAdd();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedItem]);

  useEffect(() => {
    // Check if selected item already exists in the table
    if (selectedItem) {
      const exists =
        formData.stepManualAdd.entities.some(
          (entity) => entity.entity_id === selectedItem.value
        ) ||
        formData.stepManualAdd.entitiesSelected.some(
          (entity) => entity.entity_id === selectedItem.value
        );
      setEntityExists(exists);
    }
  }, [
    selectedItem,
    formData.stepManualAdd.entities,
    formData.stepManualAdd.entitiesSelected,
  ]);

  const handleAdd = () => {
    if (selectedItem) {
      let fullEntity = allEntities.find((entity) => {
        return entity.entity_id === selectedItem.value;
      });
      const exists =
        formData.stepManualAdd.entities.some(
          (entity) => entity.entity_id === selectedItem.value
        ) ||
        formData.stepManualAdd.entitiesSelected.some(
          (entity) => entity.entity_id === selectedItem.value
        );

      if (exists) {
        setEntityExists(true);
        return;
      }

      // Update form data first
      setFormData((prevData) => ({
        ...prevData,
        stepManualAdd: {
          entitiesSelected: [
            ...prevData.stepManualAdd.entitiesSelected,
            fullEntity,
          ],
          entities: [...prevData.stepManualAdd.entities, fullEntity],
        },
      }));

      // Reset selection states
      setSelectedItem(null);
      setInputValue("");
      setEntityExists(false);

      // Force re-render of Autocomplete to ensure input is cleared
      setResetKey((prevKey) => prevKey + 1);
    }
  };
  const entityOptions = useMemo(
    () =>
      allEntities
        .map((option) => ({
          label: option.display_name,
          value: option.entity_id,
          type: option.entity_type,
          original_unique_id: option.original_unique_id,
          unique_id: option.unique_id,
        }))
        .sort((a, b) =>
          (a.label || "")
            .toLowerCase()
            .localeCompare((b.label || "").toLowerCase())
        ),
    [allEntities]
  );

  return (
    <Box>
      <Grid container justifyContent={"center"} direction={"column"} gap={3}>
        <AssetDetailsBox
          setFormData={setFormData}
          formData={formData}
          assetToEdit={assetToEdit}
        />
        {assetToEdit?.asset?.cls !== AssetClass.SMART && (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="subtitle2">
                Add Entities <span style={{ color: colors.error_main }}>*</span>
              </Typography>
              <Typography variant="body2" color={colors.text_secondary}>
                Add entities that are part of this asset
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Autocomplete
                  key={resetKey}
                  ref={autocompleteRef}
                  options={entityOptions}
                  value={selectedItem}
                  inputValue={inputValue}
                  onInputChange={handleInputChange}
                  onChange={(event, newValue) => {
                    setSelectedItem(newValue);
                  }}
                  clearOnBlur={false}
                  selectOnFocus
                  filterOptions={(options, state) => {
                    // Only filter when input length is 3 or more
                    if (state.inputValue.length < 3) {
                      return [];
                    }
                    // Custom filtering logic using state.inputValue for up-to-date filtering
                    const filteredOptions = options.filter(
                      (option) =>
                        option.label
                          ?.toLowerCase()
                          .includes(state.inputValue.toLowerCase()) ||
                        option.original_unique_id
                          ?.toLowerCase()
                          .includes(state.inputValue.toLowerCase()) ||
                        option.unique_id
                          ?.toLowerCase()
                          .includes(state.inputValue.toLowerCase())
                    );
                    return filteredOptions;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.value === value.value
                  }
                  noOptionsText={
                    inputValue.length < 3 ? (
                      <Typography variant="body2">
                        Type a minimum of 3 characters to see options.
                      </Typography>
                    ) : (
                      <Typography variant="body2">
                        No matching options
                      </Typography>
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      sx={{
                        "& .MuiInputBase-root": {
                          width: "730px",
                          height: "48px",
                          fontSize: "14px", // adjust as needed
                          borderRadius: "8px",
                          border: "1px solid " + colors.divider,
                          "& .MuiInputBase-root:hover": {
                            border: "1px solid " + colors.divider,
                          },
                        },

                        "& .MuiOutlinedInput-notchedOutline": {
                          borderRadius: "8px",
                          borderColor: colors.divider2,
                        },
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            border: "0.5px solid " + colors.divider2,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            border: "0.5px solid " + colors.grey_500,
                          },
                        },

                        "& .MuiInputBase-input::placeholder": {
                          color: colors.text_primary,
                        },
                        width: "100%",
                      }}
                      error={entityExists}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRounded />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <React.Fragment>
                            {fetchedEntitiesAreLoading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                      placeholder="Type email, or part of a name"
                    />
                  )}
                  renderOption={(props, option) => {
                    const Icon = iconMapping[option.type];
                    return (
                      <li {...props} key={option.value}>
                        <ListItemIcon>
                          <SvgToIcon IconLogo={Icon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2">
                              {option.label}
                            </Typography>
                          }
                        />
                      </li>
                    );
                  }}
                />
                <TextButton
                  onClick={handleAdd}
                  color="primary"
                  size="medium"
                  style={{ textTransform: "none", height: "48px" }}
                  disabled={selectedItem == null || entityExists}
                >
                  Add Entity
                </TextButton>
              </Box>
              {entityExists && (
                <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                  This entity is already in the table
                </Typography>
              )}
            </Box>
          </>
        )}
        <Grid item sx={{ width: "100%" }}>
          <EntitiesAssetWizardTable
            formData={formData}
            setFormData={setFormData}
            assetToEdit={assetToEdit}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default ManualAddStep;
